import React, { useMemo, useEffect, useCallback, useState } from "react";
import { observer } from "mobx-react-lite";
import { useHistory } from "react-router-dom";
import { diff } from "json8-patch";
import { FormikHelpers } from "formik";

import DisplayDateCreated from "../../../components/data-components/DisplayDateCreated";
import DisplayCreatedBy from "../../../components/data-components/DisplayCreatedBy";
import { EditDynamicProperties } from "../../../components/data-components/EditDynamicProperties";
import {
  DisplaySewerObjectId,
  DisplayDataOwner,
  DisplayInProject,
  DisplayOpenJobs,
} from "../components/DataComponents";
import { SubSection, DataSection } from "../../../components/data-components/sections/DataSections";
import PageLayout from "../../_layout/PageLayout";
import Form from "../../../components/form/Form";
import { StructureDynamicMenuItemsPages, StructureMenuItemsModel } from "./StructureMenuItems";
import {
  EditSewerObject,
  EditSewerObjectFromAssignedProject,
} from "../../../services/authorization/Permissions";
import useErrorHandler, {
  ApplicationError,
} from "../../../services/error-handler/ErrorHandlerService";
import { permissionsService } from "../../../services/authorization/UserPermissionsService";
import useUrlParams from "../../../services/url/UrlParamsService";
import { api } from "../../../services/api/ApiService";
import { CurrentStructureStorageModel } from "../../../services/sewer-object/CurrentStructureStorage";
import { warningLeavePageService } from "../../../services/page/WarningLeavePageService";
import { navigatePath } from "../../../helpers/StringHelpers";
import useRouteService from "../../../services/route-service/RouteService";
import { EntityTypeFromPath } from "../../../services/breadcrumbs/EntityTypeFromPathModel";
import { breadcrumbsStorage } from "../../../services/breadcrumbs/BreadcrumbsStorage";
import { FieldsFromBM } from "../../../api-models/response/BluemetricFields";

export default observer(() => {
  const history = useHistory();
  const urlParamsService = useUrlParams();
  const { handleErrorInSnackbar } = useErrorHandler();
  const { isUserOnProjects } = useRouteService();

  const structureId = useMemo(() => urlParamsService.getStructureId()!, [urlParamsService]);

  const [currentStructureStorage] = useState(() =>
    CurrentStructureStorageModel.create({
      structureId: structureId,
      parentEntityFromPath: {
        id: urlParamsService.getSewerObjectParentId()!,
        type: isUserOnProjects() ? EntityTypeFromPath.Project : EntityTypeFromPath.Job
      }
    }, { api })
  );

  const [structureMenuItems] = useState(() =>
    StructureMenuItemsModel.create({
      structureId: structureId,
      editMode: true,
      dynamicMenuItemPage: StructureDynamicMenuItemsPages.EditStructure,
      parentIdFromPath: currentStructureStorage.parentEntityFromPath.id,
    }, { api })
  );

  const menuItems = {
    staticItems: structureMenuItems.getStaticMenuItems(),
  };

  const canUserEditStructure = useMemo(
    () =>
      permissionsService.userHasOneOfThePermissions([
        EditSewerObject,
        EditSewerObjectFromAssignedProject,
      ]),
    []
  );

  const goToStructurePage = useCallback(
    () => history.push(`${navigatePath(history.location.pathname, -1)}`),
    [history]
  );

  const getInitialValues = useCallback(() => {
    return currentStructureStorage.currentStructure.getDynamicPropertiesKeyValueForEdit();
  }, [currentStructureStorage.currentStructure]);

  const handleSubmit = async (
    updatedDynamicProperties: FieldsFromBM,
    formActions: FormikHelpers<FieldsFromBM>
  ) => {
    try {
      const cleanedUpdatedDynamicProperties =
        currentStructureStorage.currentStructure.getDynamicPropertiesWithDecimalsAndDateFormatted(
          updatedDynamicProperties
        );
      let patchJson = diff(getInitialValues(), cleanedUpdatedDynamicProperties);
      await currentStructureStorage.patchDetails(patchJson);
      warningLeavePageService.setIsDirty(false);
      return goToStructurePage();
    } catch (error) {
      const applicationError = error as ApplicationError;
      applicationError.withForm(updatedDynamicProperties, formActions);
      applicationError.withErrorMessage("default-error.edit");
      handleErrorInSnackbar(applicationError);
    }
  };

  useEffect(() => {
    async function loadBreadcrumbsData() {
      breadcrumbsStorage.setEntityNames([{
        entity: currentStructureStorage.parentEntityFromPath.type,
        name: await currentStructureStorage.getParentEntityName(currentStructureStorage.currentStructure.project.name)

        }, {
        entity: EntityTypeFromPath.SewerObject,
        name: currentStructureStorage.currentStructure.name
        }
      ]);

      breadcrumbsStorage.updateBreadcrumbs(history.location.pathname);
    }

    loadBreadcrumbsData();
  }, [currentStructureStorage, currentStructureStorage.currentStructure, history.location.pathname]);

  return (
    <PageLayout
      accessWithOneOfThePermissions={[EditSewerObject, EditSewerObjectFromAssignedProject]}
      titleKey="EditStructure.title"
      titleContext={{ structureName: currentStructureStorage.currentStructure.name }}
      id="edit-structure"
      menuItems={menuItems}
      isLoading={currentStructureStorage.isLoading}
    >
      <Form
        formikOptions={{
          initialValues: getInitialValues(),
          onSubmit: handleSubmit,
          validateOnBlur: false,
          validateOnChange: false,
          enableReinitialize: true,
        }}
        canSubmit={canUserEditStructure}
        onCancel={goToStructurePage}
      >
        {({ values, setFieldValue }) => {
          return (
            <>
              <DataSection labelKey="general.structure.details">
                <SubSection>
                  <DisplaySewerObjectId
                    id={currentStructureStorage.currentStructure.name}
                    labelKey="sewer-objects.DisplayStructureId.label"
                  />
                  <DisplayDataOwner
                    dataOwner={currentStructureStorage.currentStructure.dataOwner}
                  />
                </SubSection>
                <SubSection>
                  <DisplayInProject project={currentStructureStorage.currentStructure.project} />
                  <DisplayOpenJobs openJobs={currentStructureStorage.currentStructure.openJobs} />
                </SubSection>
                <SubSection>
                  <DisplayDateCreated date={currentStructureStorage.currentStructure.created} />
                  <DisplayCreatedBy
                    createdBy={currentStructureStorage.currentStructure.createdBy.name}
                  />
                </SubSection>
              </DataSection>
              <EditDynamicProperties
                labelKey="general.characteristics"
                properties={currentStructureStorage.currentStructure.getDynamicPropertiesForEdit()}
                values={values}
                setFieldValue={setFieldValue}
              />
            </>
          );
        }}
      </Form>
    </PageLayout>
  );
});
